import React, { useEffect, useState } from 'react';
import Modal from './ModalTrue';
import './AuditTrailModal.css';

type ClientClaim = {
  id: string;
  lender: string;
  agreement: string;
  status: string;
  created: string;
  signup: string;
  inheritsFrom: string;
};

type ClientClaimsResponse = {
  clientId: string;
  name: string;
  emails: string[];
  claims: ClientClaim[];
};

type Props = {
  isOpen: boolean;
  claimId: string;
  onClose: () => void;
};

const apiUrl = () => process.env.REACT_APP_API_URL || 'http://localhost:4000';

const filenameFromDisposition = (header: string | null, fallback: string): string => {
  if (!header) return fallback;
  const utf8 = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8) {
    try { return decodeURIComponent(utf8[1]); } catch { /* fall through */ }
  }
  const plain = header.match(/filename="?([^";]+)"?/i);
  return plain ? plain[1] : fallback;
};

/**
 * "Get audit trail" from the claim Actions menu: lists the client's claims, the user ticks which ones, and the
 * backend returns a zip (signing summary PDF, emails spreadsheet, signed LOA/DBA, provider audit trail) which the
 * browser saves to Downloads. Read-only - nothing is sent and nothing on the claim changes.
 */
const AuditTrailModal: React.FC<Props> = ({ isOpen, claimId, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ClientClaimsResponse | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState('');

  useEffect(() => {
    if (!isOpen || !claimId) return;
    let cancelled = false;
    setLoading(true); setError(''); setResult(''); setData(null);
    const token = localStorage.getItem('token');
    fetch(`${apiUrl()}/ui/audit-trail/claims/${encodeURIComponent(claimId)}/client-claims`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (r) => {
        if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`);
        return r.json() as Promise<ClientClaimsResponse>;
      })
      .then((d) => {
        if (cancelled) return;
        setData(d);
        setSelected(new Set(d.claims.map((c) => c.id)));
      })
      .catch((e: Error) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isOpen, claimId]);

  if (!isOpen) return null;

  const allSelected = !!data && selected.size === data.claims.length;
  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (!data) return;
    setSelected(allSelected ? new Set() : new Set(data.claims.map((c) => c.id)));
  };

  const run = async () => {
    if (!data || selected.size === 0) return;
    setRunning(true); setError(''); setResult('');
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl()}/ui/audit-trail/zip`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ claimId, claimIds: Array.from(selected) }),
      });
      if (!response.ok) {
        let message = `Audit trail failed (HTTP ${response.status})`;
        try { message = (await response.json()).error || message; } catch { /* keep */ }
        throw new Error(message);
      }
      const blob = await response.blob();
      const filename = filenameFromDisposition(response.headers.get('Content-Disposition'), `audit-trail-${claimId}.zip`);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      const files = response.headers.get('X-Audit-Trail-Files');
      const warnings = Number(response.headers.get('X-Audit-Trail-Warnings') || '0');
      setResult(`Downloaded ${filename}${files ? ` (${files} files)` : ''}${warnings ? ` - ${warnings} note(s) inside the summary PDF` : ''}.`);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setRunning(false);
    }
  };

  return (
    <Modal onClose={onClose} contentClassName="audit-trail-modal-container">
      <div className="audit-trail-modal">
        <div className="audit-trail-modal__header">
          <div>
            <h2>Get audit trail</h2>
            {data && (
              <p className="audit-trail-modal__sub">
                {data.name || 'Client'} &middot; {data.emails.join(', ')} &middot; {data.claims.length} claim{data.claims.length === 1 ? '' : 's'}
              </p>
            )}
          </div>
          {data && (
            <div className="audit-trail-modal__actions">
              <button type="button" className="audit-trail-modal__secondary" onClick={onClose}>Close</button>
              <button type="button" className="audit-trail-modal__primary" onClick={run} disabled={running || selected.size === 0}>
                {running ? 'Building… up to a minute' : `Get audit trail (${selected.size} claim${selected.size === 1 ? '' : 's'})`}
              </button>
            </div>
          )}
        </div>
        {loading && <p>Loading the client&rsquo;s claims&hellip;</p>}
        {error && <div className="audit-trail-modal__error">{error}</div>}
        {data && (
          <>
            <div className="audit-trail-modal__body">
            <p className="audit-trail-modal__hint">
              Tick the claims to include. The zip holds a signing summary, every email sent to and received from the client,
              lender correspondence, the signed LOA and DBA, and the e-signature provider&rsquo;s audit trail where held.
            </p>
            <table className="audit-trail-modal__table">
              <thead>
                <tr>
                  <th><input type="checkbox" checked={allSelected} onChange={toggleAll} aria-label="Select all claims" /></th>
                  <th>Claim</th>
                  <th>Lender</th>
                  <th>Agreement</th>
                  <th>Created</th>
                  <th>Signed up via</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {data.claims.map((c) => (
                  <tr key={c.id} className={c.id === claimId ? 'audit-trail-modal__row--current' : ''}>
                    <td><input type="checkbox" checked={selected.has(c.id)} onChange={() => toggle(c.id)} aria-label={`Include ${c.id}`} /></td>
                    <td>{c.id}</td>
                    <td>{c.lender}</td>
                    <td>{c.agreement}</td>
                    <td>{c.created}</td>
                    <td>{c.signup}{c.inheritsFrom ? ` (signing from ${c.inheritsFrom})` : ''}</td>
                    <td>{c.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            {result && <div className="audit-trail-modal__result">{result}</div>}
            {running && <div className="audit-trail-modal__result">Building the audit trail&hellip; this takes up to a minute. You can close this window and carry on; the zip will still save to your Downloads folder when it is ready.</div>}
          </>
        )}
      </div>
    </Modal>
  );
};

export default AuditTrailModal;
